/**
 * Capacitor integration for native iOS features.
 * Gracefully degrades to no-ops on web.
 */
import { Capacitor } from '@capacitor/core';

import { api } from '@/lib/api';

export const isNative = Capacitor.isNativePlatform();
export const isIOS = Capacitor.getPlatform() === 'ios';
export const isWeb = Capacitor.getPlatform() === 'web';

export async function initCapacitor(): Promise<void> {
  if (!isNative) return;
  setupAppLifecycle();
  setupDeepLinkHandler();
  setupBrowserFinishedHandler();
}

export async function registerForPush(): Promise<string | null> {
  if (!isNative) return null;
  try {
    const { PushNotifications } = await import('@capacitor/push-notifications');
    const permission = await PushNotifications.requestPermissions();
    if (permission.receive !== 'granted') return null;
    await PushNotifications.register();
    return new Promise<string | null>((resolve) => {
      const timeout = setTimeout(() => resolve(null), 10_000);
      PushNotifications.addListener('registration', (token) => {
        clearTimeout(timeout);
        resolve(token.value);
      });
      PushNotifications.addListener('registrationError', () => {
        clearTimeout(timeout);
        resolve(null);
      });
    });
  } catch { return null; }
}

export function onPushTapped(callback: (data: Record<string, string>) => void): () => void {
  if (!isNative) return () => {};
  let cleanup = () => {};
  import('@capacitor/push-notifications').then(({ PushNotifications }) => {
    const listener = PushNotifications.addListener('pushNotificationActionPerformed', (action) => {
      callback(action.notification.data);
    });
    cleanup = () => listener.then(l => l.remove());
  });
  return () => cleanup();
}

function setupAppLifecycle(): void {
  import('@capacitor/app').then(({ App }) => {
    App.addListener('appStateChange', async ({ isActive }) => {
      console.log('[Capacitor] App state:', isActive ? 'foreground' : 'background');
      if (isActive) {
        // BP-061 + BP-076 FIX: Clear BOTH Notification Center AND Home Screen badge
        try {
          const { PushNotifications } = await import('@capacitor/push-notifications');
          await PushNotifications.removeAllDeliveredNotifications();
        } catch { /* non-critical */ }
        try {
          const { Badge } = await import('@capawesome/capacitor-badge');
          await Badge.clear();
        } catch { /* non-critical */ }
        // BP-091: Dispatch foreground event for SSE reconnection
        // More reliable than visibilitychange on iOS native
        window.dispatchEvent(new Event('capacitor-foreground'));
      }
    });
  });
}

/**
 * BP-090: Fallback OAuth handler when SFSafariViewController is dismissed.
 * Primary path: appUrlOpen deep link fires when custom URL scheme works.
 * Fallback: if deep link didn't fire, browserFinished fires when user
 * taps "Done" or Browser.close() is called. Exchange the sid stored
 * in localStorage.
 */
function setupBrowserFinishedHandler(): void {
  import('@capacitor/browser').then(({ Browser }) => {
    Browser.addListener('browserFinished', () => {
      console.log('[Capacitor] Browser finished');
      // Small delay to let deep link handler fire first if it's going to
      setTimeout(() => {
        const sid = localStorage.getItem('__pa_oauth_sid');
        const ts = localStorage.getItem('__pa_oauth_ts');
        if (sid && ts) {
          const elapsed = Date.now() - parseInt(ts, 10);
          localStorage.removeItem('__pa_oauth_sid');
          localStorage.removeItem('__pa_oauth_ts');
          if (elapsed < 120_000) {
            console.log('[Capacitor] Browser closed with pending OAuth — exchanging sid (fallback)...');
            window.location.href = `/auth/exchange?code=${encodeURIComponent(sid)}`;
          }
        }
      }, 500);
    });
  });
}

/**
 * BP-086: Handle deep links from custom URL scheme (pamanagement://).
 * After OAuth completes in the system browser, iOS routes the callback
 * URL back to the app. We extract the one-time code and navigate the
 * WKWebView to /auth/exchange to set the JWT cookie.
 */
function setupDeepLinkHandler(): void {
  import('@capacitor/app').then(({ App }) => {
    App.addListener('appUrlOpen', async ({ url }) => {
      console.log('[Capacitor] Deep link received:', url);
      try {
        const parsed = new URL(url);
        // Handle: pamanagement://auth/callback?code=...
        if (parsed.hostname === 'auth' && parsed.pathname === '/callback') {
          const code = parsed.searchParams.get('code');
          if (code) {
            console.log('[Capacitor] OAuth code received, exchanging for cookie...');
            // Clear pending OAuth state (deep link path succeeded)
            localStorage.removeItem('__pa_oauth_sid');
            localStorage.removeItem('__pa_oauth_ts');
            // Close SFSafariViewController if still open
            try {
              const { Browser } = await import('@capacitor/browser');
              await Browser.close();
            } catch { /* browser may already be closed */ }
            window.location.href = `/auth/exchange?code=${encodeURIComponent(code)}`;
            return;
          }
        }
        console.warn('[Capacitor] Unhandled deep link:', url);
      } catch (e) {
        console.error('[Capacitor] Failed to parse deep link URL:', e);
      }
    });
  });
}

/**
 * Unsubscribe device token on logout. BP-060 FIX.
 * Call before clearing auth state.
 */
export async function unregisterPush(): Promise<void> {
  if (!isNative) return;
  const savedToken = localStorage.getItem('__pa_push_token');
  if (!savedToken) return;
  try {
    await api.post('/api/notifications/unsubscribe', { token: savedToken });
    localStorage.removeItem('__pa_push_token');
  } catch {
    // Best effort — token will be overwritten on next login via BP-058
  }
}

export async function haptic(style: 'light' | 'medium' | 'heavy' = 'medium'): Promise<void> {
  if (!isNative) return;
  const { Haptics, ImpactStyle } = await import('@capacitor/haptics');
  const map = { light: ImpactStyle.Light, medium: ImpactStyle.Medium, heavy: ImpactStyle.Heavy };
  await Haptics.impact({ style: map[style] });
}
